import React, { useState, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
} from "@mui/material"
import { SECTION_PERMISSIONS, hasPermission, isB2BUser, isRetailUser, hasSidebarPermission } from "../constants/permissions"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import InventoryIcon from "@mui/icons-material/Inventory"
import CreditScoreIcon from "@mui/icons-material/CreditScore"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PaymentsIcon from "@mui/icons-material/Payments"
import DashboardIcon from "@mui/icons-material/Dashboard"
import PeopleIcon from "@mui/icons-material/People"
import BusinessIcon from "@mui/icons-material/Business"
import CategoryIcon from "@mui/icons-material/Category"
import BadgeIcon from "@mui/icons-material/Badge"
import ReceiptIcon from "@mui/icons-material/Receipt"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import SettingsIcon from "@mui/icons-material/Settings"
import BarChartIcon from "@mui/icons-material/BarChart"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import SpeedIcon from "@mui/icons-material/Speed"
import VpnKeyIcon from "@mui/icons-material/VpnKey"
import RateReviewIcon from "@mui/icons-material/RateReview"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import logo from "../assets/images/logo.png"

const MuiSidebarContent = ({ onItemClick }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const theme = useTheme()
  const logoHeight = theme.layoutConfig?.sidebarLogoHeight || 70
  const isEnterprise = logoHeight < 70
  const isEliton = !!theme.eliton
  const isCompact = isEnterprise || isEliton
  const [openMenus, setOpenMenus] = useState({})

  // Get user permissions from Redux (role-based, from each app's own roles table)
  const permissions = useSelector(state => state.Login?.permissions || {})

  const handleMenuToggle = (menuId) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }))
  }

  const isActive = (path) => {
    // Handle paths with query parameters
    if (path.includes("?")) {
      const [pathname, query] = path.split("?")
      const currentFullPath = location.pathname + location.search
      return currentFullPath === path || (location.pathname === pathname && location.search === "?" + query)
    }
    // For paths without query params, also check if current path has no query params
    if (location.pathname === path && !location.search) return true
    return false
  }

  const menuSections = [
    {
      title: t("Quick Access"),
      items: [
        {
          id: "quickview",
          icon: <SpeedIcon />,
          label: t("Quick View"),
          path: "/quick-view",
        },
      ],
    },
    {
      title: t("Sales"),
      items: [
        {
          id: "orders",
          icon: <InventoryIcon />,
          label: t("Orders"),
          children: [
            { path: "/orders", label: t("Orders") },
            { path: "/createorder", label: t("Create Order") },
          ],
        },
        {
          id: "order-reviews",
          icon: <RateReviewIcon />,
          label: t("Quote Reviews"),
          path: "/order-reviews",
        },
        {
          id: "customers",
          icon: <PeopleIcon />,
          label: t("Customers"),
          children: [
            { path: "/customers", label: t("Customers") },
            { path: "/createcustomer", label: t("Create Customer") },
          ],
        },
        {
          id: "payments",
          icon: <PaymentsIcon />,
          label: t("Payments"),
          children: [
            { path: "/payments-list/", label: t("Payments") },
            { path: "/createpayment", label: t("Create Payment") },
          ],
        },
        {
          id: "deliveries",
          icon: <LocalShippingIcon />,
          label: t("Deliveries"),
          children: [{ path: "/shipping", label: t("Shipping") }],
        },
        {
          id: "loans",
          icon: <CreditScoreIcon />,
          label: t("Loans"),
          children: [{ path: "/Loans", label: t("Loans") }],
        },
      ],
    },
    {
      title: t("Expenses"),
      items: [
        {
          id: "invoices",
          icon: <ReceiptIcon />,
          label: t("Εισερχόμενα"),
          children: [
            { path: "/invoices", label: t("Εισερχόμενα") },
            { path: "/invoice-create", label: t("Create Invoice") },
          ],
        },
        {
          id: "suppliers",
          icon: <BusinessIcon />,
          label: t("Suppliers"),
          children: [
            { path: "/providers", label: t("Suppliers") },
            { path: "/createprovider", label: t("Create Supplier") },
          ],
        },
        {
          id: "salesmans",
          icon: <BadgeIcon />,
          label: t("Salesmans"),
          children: [
            { path: "/salespersons", label: t("Salesmans") },
            { path: "/createsalesperson", label: t("Create Salesman") },
          ],
        },
      ],
    },
    {
      title: t("Production"),
      items: [
        {
          id: "products",
          icon: <CategoryIcon />,
          label: t("Products"),
          children: [
            { path: "/products", label: t("Products") },
            { path: "/createproduct", label: t("Create Product") },
          ],
        },
        {
          id: "operations",
          icon: <PrecisionManufacturingIcon />,
          label: t("Operations"),
          children: [
            { path: "/operations", label: t("Operations") },
          ],
        },
        {
          id: "packaging",
          icon: <InventoryIcon />,
          label: t("Packaging"),
          children: [
            { path: "/packaging", label: t("Packaging") },
          ],
        },
        {
          id: "sections",
          icon: <DashboardIcon />,
          label: t("Sections"),
          children: [
            { path: "/sections", label: t("Sections") },
          ],
        },
      ],
    },
    {
      title: t("Analytics"),
      items: [
        {
          id: "dashboards",
          icon: <DashboardIcon />,
          label: t("Dashboards"),
          children: [
            { path: "/analytics", label: t("Analytics Dashboard") },
            { path: "/dashboard", label: t("Customer Balance") },
            { path: "/dashboardproviders", label: t("Provider Balance") },
            { path: "/dashboardproducts", label: t("Top Products") },
            { path: "/dashboardmaterials", label: t("Top Materials") },
          ],
        },
        {
          id: "financials",
          icon: <AccountBalanceWalletIcon />,
          label: t("Financials"),
          children: [
            { path: "/economics", label: t("Economics") },
            { path: "/cashregister", label: t("Cash Register") },
          ],
        },
        {
          id: "settings",
          icon: <SettingsIcon />,
          label: t("Settings"),
          children: [
            { path: "/settings-configuration", label: t("Configuration") },
            { path: "/settings-backup", label: t("Backup & Import") },
          ],
        },
      ],
    },
    {
      title: t("Cloud Licences"),
      items: [
        {
          id: "cloudlicences",
          icon: <VpnKeyIcon />,
          label: t("Licences"),
          path: "/cloud-licences",
        },
      ],
    },
    {
      title: t("Info"),
      items: [
        {
          id: "about",
          icon: <InfoOutlinedIcon />,
          label: t("About"),
          path: "/about",
        },
      ],
    },
  ]

  // Filter menu sections based on user role permissions only
  // (enabled_modules from the central apps table is for backend GUI admin, not app users)
  const filteredMenuSections = useMemo(() => {
    // B2B users see no sidebar at all
    if (isB2BUser(permissions)) return []

    // Retail users only see Quote
    if (isRetailUser(permissions)) return [{
      title: t("Quote"),
      items: [{
        id: "quote",
        icon: <RateReviewIcon />,
        label: t("Quote"),
        path: "/quote",
      }],
    }]

    return menuSections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        return hasSidebarPermission(permissions, item.id)
      })
    })).filter(section => section.items.length > 0)
  }, [permissions, menuSections])

  const renderMenuItem = (item) => {
    const isOpen = openMenus[item.id]
    const hasActiveChild = item.children?.some((child) => isActive(child.path))

    // Handle items with direct path (no children)
    if (item.path) {
      const isItemActive = isActive(item.path)
      return (
        <ListItemButton
          key={item.id}
          component={Link}
          to={item.path}
          onClick={onItemClick}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            ...(isItemActive && {
              backgroundColor: isEliton ? "rgba(229, 0, 25, 0.15)" : "rgba(85, 110, 230, 0.1)",
              color: isEliton ? "#ff6b6b" : "primary.main",
            }),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: isItemActive ? (isEliton ? "#ff6b6b" : "primary.main") : "inherit",
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: "0.8125rem",
              fontWeight: isItemActive ? 600 : 400,
            }}
          />
        </ListItemButton>
      )
    }

    return (
      <React.Fragment key={item.id}>
        <ListItemButton
          onClick={() => handleMenuToggle(item.id)}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            ...(hasActiveChild && {
              backgroundColor: isEliton ? "rgba(229, 0, 25, 0.15)" : "rgba(85, 110, 230, 0.1)",
              color: isEliton ? "#ff6b6b" : "primary.main",
            }),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: hasActiveChild ? (isEliton ? "#ff6b6b" : "primary.main") : "inherit",
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: "0.8125rem",
              fontWeight: hasActiveChild ? 500 : 400,
            }}
          />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children?.map((child) => (
              <ListItemButton
                key={child.path}
                component={Link}
                to={child.path}
                onClick={onItemClick}
                sx={{
                  pl: 6,
                  py: 0.75,
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  ...(isActive(child.path) && {
                    backgroundColor: isEliton ? "rgba(229, 0, 25, 0.15)" : "rgba(85, 110, 230, 0.1)",
                    color: isEliton ? "#ff6b6b" : "primary.main",
                  }),
                }}
              >
                <ListItemText
                  primary={child.label}
                  primaryTypographyProps={{
                    fontSize: "0.8125rem",
                    fontWeight: isActive(child.path) ? 500 : 400,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  return (
    <Box sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      ...(isEliton && {
        bgcolor: "#212121",
        color: "rgba(255,255,255,0.85)",
        "& .MuiListItemButton-root": {
          color: "rgba(255,255,255,0.75)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        },
        "& .MuiListItemIcon-root": { color: "rgba(255,255,255,0.5)" },
        "& .MuiSvgIcon-root": { color: "inherit" },
        "& .MuiCollapse-root .MuiListItemButton-root": {
          color: "rgba(255,255,255,0.6)",
        },
      }),
    }}>
      {/* Logo and Brand Name */}
      <Box
        component={Link}
        to={isEliton ? "/about" : "/"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isEliton ? "center" : "flex-start",
          gap: isEliton ? 1 : 0,
          pl: isEliton ? 2 : isEnterprise ? 3 : 4.5,
          pr: 1.5,
          py: isCompact ? 0.5 : 2,
          pt: isCompact ? 0.5 : 1.5,
          textDecoration: "none",
          borderBottom: 1,
          borderColor: isEliton ? "rgba(255,255,255,0.08)" : "divider",
          minHeight: logoHeight,
        }}
      >
        {isEliton ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={82} height={25} viewBox="0 0 114.882 34.923">
              <defs><clipPath id="eliton-sidebar-clip"><rect width="114.882" height="34.923" fill="none" /></clipPath></defs>
              <g transform="translate(0 0)">
                <rect width="3.844" height="26.376" transform="translate(41.399 7.835)" fill="#fff" />
                <g clipPath="url(#eliton-sidebar-clip)">
                  <path d="M66.085,3.8l-.1-.584L65.7,2.681l-.438-.438L64.772,2l-.633-.1-.584.1-.536.243-.389.438-.292.535-.1.584.1.584A1.91,1.91,0,0,0,65.7,4.92l.292-.535Z" transform="translate(-20.843 -0.637)" fill="#fff" />
                  <rect width="3.796" height="34.211" transform="translate(31.909)" fill="#fff" />
                  <path d="M22.857,22.815q-.219-.656-.438-1.314a9.706,9.706,0,0,0-17.812,0l-.486,1.314Zm-18.979,3.8c1.478,8.576,11.592,11.457,17.13,4.964l.779-1.168h4.283a24.9,24.9,0,0,1-1.606,2.871A13.516,13.516,0,0,1,.082,23.788q.146-.8.292-1.606C4.394,7.887,24.3,8.659,26.9,23.4a18.568,18.568,0,0,1,.049,3.212H3.878" transform="translate(0 -3.982)" fill="#fff" />
                  <path d="M80.378,26.133l.1.925A4.874,4.874,0,0,0,85.1,31.049l.925-.1V34.8l-1.168.049A8.77,8.77,0,0,1,76.631,27.2l-.049-1.07V0h3.8V8.273h5.645V12.02H80.378Z" transform="translate(-25.646 0)" fill="#fff" />
                  <path d="M115.157,20.182l-.827-1.119a9.737,9.737,0,0,0-17.082,7.251l.243,1.363-3.552,1.508L93.6,27.579a13.52,13.52,0,0,1,24.187-10.268l.876,1.363Z" transform="translate(-31.289 -3.963)" fill="#e50019" />
                  <path d="M117.6,28.946l.146,1.363a9.739,9.739,0,0,1-16.692,7.056l-.876-1.022L96.63,37.852l.973,1.314a13.509,13.509,0,0,0,23.846-10.122l-.292-1.606Z" transform="translate(-32.359 -9.189)" fill="#fff" />
                  <path d="M143.668,23.266l.1-1.119a7.119,7.119,0,0,1,14.064,0l.1,1.119V38.353h3.8V23.266L161.673,22c-2.1-13.495-21.115-12.534-21.8,1.12V38.353h3.8Z" transform="translate(-46.84 -4.141)" fill="#fff" />
                </g>
              </g>
            </svg>
            <Typography
              sx={{
                fontSize: "0.58rem",
                fontWeight: 500,
                color: "rgba(255,255,255,0.35)",
                letterSpacing: "0.02em",
                lineHeight: 1.2,
              }}
            >
              by<br />FlexFlow
            </Typography>
          </Box>
        ) : (
          <>
            <img
              src={logo}
              alt="FlexFlow"
              style={{
                height: isEnterprise ? 28 : 38,
                objectFit: "contain",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                ml: isEnterprise ? -4 : -5,
                fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 600,
                fontSize: isEnterprise ? "1.1rem" : "1.5rem",
                letterSpacing: "0.04em",
                background: isEnterprise ? "none" : "linear-gradient(135deg, #10b981 0%, #3b82f6 100%)",
                WebkitBackgroundClip: isEnterprise ? "unset" : "text",
                WebkitTextFillColor: isEnterprise ? "#334155" : "transparent",
                backgroundClip: isEnterprise ? "unset" : "text",
              }}
            >
              FlexFlow
            </Typography>
          </>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: "auto", py: 1 }}>
        {filteredMenuSections.map((section) => (
          <React.Fragment key={section.title}>
            <Typography
              variant="overline"
              sx={{
                px: 3,
                py: isCompact ? 1 : 1.5,
                display: "block",
                color: isEliton ? "rgba(255,255,255,0.4)" : "text.secondary",
                fontSize: isCompact ? "0.6rem" : "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
              }}
            >
              {section.title}
            </Typography>
            <List component="nav" disablePadding>
              {section.items.map(renderMenuItem)}
            </List>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}

export default MuiSidebarContent
